import { useEffect, memo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isStorageEvent, isStorageClear } from '@features/authentication/typeGuards';
import { getAppScope } from '@features/authentication/authFunctions';
import { TOKEN_KEY_PREFIX, parsePublicAuthToken } from '@features/authentication/storageFunctions';
import { AppModeEnum } from '@project-types/enums';
import { User } from '@project-types/User';
import { setUser, setAppMode } from '@store/global/slice';
import { selectUser } from '@store/global/selectors';



const AuthComponent = () => {

	const user = useSelector(selectUser);
	const dispatch = useDispatch();
	const saveUser = (user: User | null) => dispatch(setUser(user));
	const changeAppMode = (mode: AppModeEnum) => dispatch(setAppMode(mode));


	useEffect(() => {
		window.addEventListener('auth-token-update', updateUser);
		window.addEventListener('storage', updateUser);
		window.addEventListener('auth-token-delete-all', deleteUser);

		return () => {
			window.removeEventListener('auth-token-update', updateUser)
			window.removeEventListener('storage', updateUser)
			window.removeEventListener('auth-token-delete-all', deleteUser)
		};
	}, []);


	useEffect(() => {
		user
		? changeAppMode(AppModeEnum.authSuccess)
		: changeAppMode(AppModeEnum.authStart)
	}, [ user ]);


	const updateUser = useCallback((event: Event) => {
		if (!isStorageEvent(event)) return;
		
		if (isStorageClear(event)) {
			saveUser(null);
			return;
		}

		if (!event.key || !event.key.startsWith(TOKEN_KEY_PREFIX)) return;
			
		if (event.newValue) {
			let parsedPublicToken = parsePublicAuthToken(event.newValue);
			if (!parsedPublicToken || !parsedPublicToken.scope) return;

			const appScope = getAppScope(parsedPublicToken);
			if (!appScope) return;

			saveUser({
				userId: parsedPublicToken.userId,
				userName: parsedPublicToken.userName,
				roles: appScope.roles,
				secureAttributes: appScope.secureAttributes,
				customFields: appScope.customFields,
			});
		}
		
		if (!event.newValue) {
			saveUser(null);
		}
	}, []);


	const deleteUser = useCallback(() => {
		saveUser(null);
	}, []);


	return null;
}

export default memo(AuthComponent);